import { AfterViewInit, ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { concatLatestFrom } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { BehaviorSubject, map } from 'rxjs';
import { first } from 'rxjs/operators';

import { CloseChat } from '../store/chat.actions';
import { getMochaChat, selectChatOpened } from '../store/chat.selectors';

import { BrowserOnly } from '@core/data-providers/platform/platform.decorators';
import { CommonWindow, ItxChat } from '@core/domain/models/common-window.model';
import { getCmsConfigValue, getPage } from '@core/infrastructure/state/core.selectors';

@Component({
    selector: 'mocha',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './mocha.component.html',
})
export class MochaComponent implements AfterViewInit {
    public selectChatOpened$ = this.store.select(selectChatOpened);

    private readonly _loading$ = new BehaviorSubject(true);

    public loading$ = this._loading$.asObservable();

    private readonly _showSelector$ = new BehaviorSubject(false);

    public showSelector$ = this._showSelector$.asObservable();

    public isPage$ = this.store.select(getPage).pipe(map((page: any) => page === 'ItxChatPage'));

    constructor(
        private readonly store: Store,
        @Inject(WINDOW) private readonly window: CommonWindow,
    ) {}

    public ngAfterViewInit(): void {
        this.store.select(getMochaChat)
            .pipe(
                first((mocha) => mocha.loaded),
                concatLatestFrom(() => this.store.select(getCmsConfigValue('chat_mocha'))),
            )
            .subscribe(([_, config]) => {
                this.initChat(config);
            });
    }

    public close(): void {
        this.store.dispatch(CloseChat());
    }

    @BrowserOnly<MochaComponent, undefined>(undefined)
    private initChat(config) {
        this.window.inditex.initChat(
            config.element,
            config.options,
            (chat: ItxChat) => {
                this.window.itxChat = chat;
                this.showSelector();
            }
        );
    }

    private restoreSession() {
        this._showSelector$.next(false);
        this.window.itxChat.restoreChatSession();
        this.window.itxChat.sessionRestored = () => {
            this._loading$.next(false);
        };
    }

    @BrowserOnly<MochaComponent, undefined>(undefined)
    private showSelector() {
        this.window.itxChat.chatReady = () => {
            this._showSelector$.next(true);
            if (this.window.inditex.isChatSessionAvailable()) {
                this.restoreSession();
            } else {
                this._loading$.next(false);
            }
        };
    }
}
